import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Modal, Offcanvas, Button, Form, Badge, Alert, Row, Col, Spinner } from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaEdit, FaLock, FaUnlock, FaSyncAlt,
  FaUsers, FaCheckCircle, FaUserShield, FaEye, FaEyeSlash,
  FaSearch, FaFilter, FaMoneyBillWave, FaShieldAlt, FaHardHat,
  FaLink, FaCheckSquare, FaRegSquare, FaKey, FaNetworkWired, FaCamera,
  FaChevronDown, FaChevronRight
} from 'react-icons/fa';
import documentService from '../services/documentService';
import Select from 'react-select';
import api from '../services/api';
import KpiCard from '../components/KpiCard';
import { checkPasswordStrength, PASSWORD_RULE_MESSAGE } from '../utils/passwordPolicy';

/* ─── SECTIONS (arbre privilèges) ─────────────────────────────────────────── */
const SECTIONS = [
  {
    label: '💰 Finance / Budget', color: '#6d4e36',
    modules: [
      { key: 'dashboard', label: 'Tableau de bord', sublabel: 'KPIs généraux',
        children: [{ key: 'dashboard:all', icon: '🌍', label: 'Tous chantiers' }] },
      { key: 'budget-realisation', label: 'Cienco Dashboard', sublabel: 'Budget vs réalisation',
        children: [{ key: 'budget-realisation:all', icon: '🌍', label: 'Tous chantiers' }] },
      { key: 'articles-suivis', label: 'Articles Critiques', sublabel: '',
        children: [
          { key: 'articles-suivis:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'articles-suivis:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'articles-suivis:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'bdc-suivis', label: 'Suivi BDC', sublabel: '',
        children: [
          { key: 'bdc-suivis:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'bdc-suivis:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'bdc-suivis:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'comptabilite', label: 'Outils Comptabilité', sublabel: '', children: [] },
    ]
  },
  {
    label: '📋 Rapports & Paie', color: '#2e7d32',
    modules: [
      { key: 'rapport-chantier', label: 'Rapport Chantier', sublabel: '',
        children: [{ key: 'rapport-chantier:all', icon: '🌍', label: 'Tous chantiers' }] },
      { key: 'paie-mois', label: 'Paie du Mois', sublabel: '',
        children: [
          { key: 'paie-mois:write', icon: '✏️', label: 'Saisir / Modifier' },
          { key: 'paie-mois:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'paie-mois:all', icon: '🌍', label: 'Tous chantiers + salaires' },
        ] },
      { key: 'resume-pointage', label: 'Résumé Pointage', sublabel: '',
        children: [{ key: 'resume-pointage:all', icon: '🌍', label: 'Tous chantiers' }] },
    ]
  },
  {
    label: '👷 Ouvriers & Pointage', color: '#1565c0',
    modules: [
      { key: 'ouvriers', label: 'Ouvriers', sublabel: 'ses chantiers uniquement',
        children: [
          { key: 'ouvriers:write', icon: '✏️', label: 'Ajouter / Modifier / Bloquer + documents' },
          { key: 'ouvriers:delete', icon: '🗑', label: 'Supprimer un ouvrier' },
          { key: 'ouvriers:all', icon: '🌍', label: 'Tous les chantiers + taux/salaires' },
        ] },
      { key: 'stc', label: 'STC — Solde de Tout Compte', sublabel: 'indemnités légales', children: [] },
      { key: 'pointages', label: 'Pointages', sublabel: '',
        children: [
          { key: 'pointages:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'pointages:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'pointages:all', icon: '🌍', label: 'Tous chantiers + salaires' },
        ] },
      { key: 'pointage-journalier', label: 'Pointage Journalier', sublabel: '',
        children: [
          { key: 'pointage-journalier:write', icon: '✏️', label: 'Saisir et enregistrer' },
          { key: 'pointage-journalier:all', icon: '🌍', label: 'Tous les chantiers' },
        ] },
    ]
  },
  {
    label: '🚜 Matériels & Carburant', color: '#7b3f00',
    modules: [
      { key: 'materiels', label: 'Matériels', sublabel: '',
        children: [
          { key: 'materiels:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'materiels:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'materiels:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'pointage-materiels', label: 'Pointage Matériel', sublabel: '',
        children: [
          { key: 'pointage-materiels:write', icon: '✏️', label: 'Saisir / Modifier' },
          { key: 'pointage-materiels:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'pointage-materiels:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'pointage-materiels-liste', label: 'Liste Pointage Matériel', sublabel: '',
        children: [{ key: 'pointage-materiels-liste:all', icon: '🌍', label: 'Tous chantiers' }] },
      { key: 'vidanges', label: 'Vidanges', sublabel: '',
        children: [
          { key: 'vidanges:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'vidanges:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'vidanges:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'citernes-gasoil', label: 'Citernes Gasoil', sublabel: '',
        children: [
          { key: 'citernes-gasoil:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'citernes-gasoil:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'citernes-gasoil:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'bdc-reparations', label: 'BDC → Réparations', sublabel: '',
        children: [
          { key: 'bdc-reparations:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'bdc-reparations:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
      { key: 'materiels-reparations', label: 'Matériels Réparations', sublabel: '',
        children: [
          { key: 'materiels-reparations:write', icon: '✏️', label: 'Ajouter / Modifier' },
          { key: 'materiels-reparations:delete', icon: '🗑', label: 'Supprimer' },
          { key: 'materiels-reparations:all', icon: '🌍', label: 'Tous chantiers' },
        ] },
    ]
  },
  {
    label: '📊 Décisionnel (Almadina)', color: '#0097a7',
    modules: [
      { key: 'decisionnel', label: 'Accès Décisionnel', sublabel: '', children: [
        { key: 'decisionnel:kpi', icon: '📈', label: 'KPIs globaux' },
        { key: 'decisionnel:lots', icon: '🏠', label: 'Statut lots vendus' },
        { key: 'decisionnel:notaire', icon: '📝', label: 'Prochainement au notaire' },
        { key: 'decisionnel:projets', icon: '📊', label: 'Performance par projet' },
        { key: 'decisionnel:commerciaux', icon: '👔', label: 'Performance commerciaux' },
        { key: 'decisionnel:graphiques', icon: '📉', label: 'Graphiques mensuels' },
        { key: 'decisionnel:recouvrement', icon: '💰', label: 'Analyse recouvrement' },
        { key: 'decisionnel:tableau', icon: '📋', label: 'Tableau synthèse' },
        { key: 'decisionnel:details', icon: '🔍', label: 'Détails ventes / rec / PN' },
      ] },
    ]
  },
  {
    label: '📱 Annuaires & Parc informatique', color: '#0d6efd',
    modules: [
      { key: 'telephones', label: 'Annuaire téléphones', sublabel: 'contacts par société',
        children: [{ key: 'telephones:write', icon: '✏️', label: 'Ajouter / modifier / importer / supprimer' }] },
      { key: 'parc-informatique', label: 'Parc informatique', sublabel: 'appareils & historique',
        children: [{ key: 'parc-informatique:write', icon: '✏️', label: 'Ajouter / modifier / affecter / importer' }] },
      { key: 'abonnement', label: 'Abonnements', sublabel: 'suivi & alertes',
        children: [
          { key: 'abonnement:write', icon: '✏️', label: 'Ajouter / modifier' },
          { key: 'abonnement:delete', icon: '🗑', label: 'Supprimer' },
        ] },
    ]
  },
];

const allModKeys = (mod) => [mod.key, ...mod.children.map(c => c.key)];
const allSecKeys = (sec) => sec.modules.flatMap(allModKeys);
const ALL_PRIV_KEYS = SECTIONS.flatMap(allSecKeys);
const ALL_READ_KEYS = SECTIONS.flatMap(s => s.modules.map(m => m.key));

const findModule = (key) => {
  for (const sec of SECTIONS) {
    const mod = sec.modules.find(m => m.key === key);
    if (mod) return mod;
  }
  return null;
};

const togglePrivKey = (key, privs) => {
  const next = new Set(privs);
  if (next.has(key)) {
    next.delete(key);
    if (!key.includes(':')) {
      const mod = findModule(key);
      if (mod) mod.children.forEach(c => next.delete(c.key));
    }
  } else {
    next.add(key);
    if (key.includes(':')) next.add(key.split(':')[0]);
  }
  return next;
};

const matchesQuery = (mod, q) => {
  if (!q) return true;
  const hay = `${mod.label} ${mod.sublabel} ${mod.children.map(c => c.label).join(' ')}`.toLowerCase();
  return hay.includes(q);
};

/* ─── constantes rôles ────────────────────────────────────────────────────── */
const ROLES = [
  { value: 'admin', label: 'Admin', color: '#dc3545', desc: 'Accès complet' },
  { value: 'responsable', label: 'Responsable', color: '#e67e22', desc: 'Gestion des chantiers et pointages' },
  { value: 'pointeur', label: 'Pointeur', color: '#0d6efd', desc: 'Saisie des pointages' },
  { value: 'responsable_budget', label: 'Resp. Budget', color: '#6f42c1', desc: 'Tableau de bord budget uniquement' },
  { value: 'achat', label: 'Achat', color: '#0d9488', desc: 'Accès aux 2 tableaux de bord' },
  { value: 'resp_commercial', label: 'Resp. Commercial', color: '#e91e63', desc: 'Dashboard commercial & immobilier' }
];

const roleInfo = Object.fromEntries(ROLES.map(r => [r.value, r]));

const RoleBadge = ({ role }) => {
  const info = roleInfo[role] || { label: role, color: '#888' };
  return (
    <span style={{
      display: 'inline-block', background: `${info.color}18`, color: info.color,
      border: `1px solid ${info.color}55`, borderRadius: 6, padding: '2px 8px',
      fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap'
    }}>
      {info.label}
    </span>
  );
};

/* ─── style helpers ───────────────────────────────────────────────────────── */
const MAIN = '#6d4e36';

const card = {
  borderRadius: 12, border: '1px solid rgba(109,78,54,0.15)',
  boxShadow: '0 2px 12px rgba(109,78,54,0.08)', background: '#fff', overflow: 'hidden'
};

const thStyle = {
  background: 'linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%)', color: '#fff',
  fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em',
  padding: '10px 12px', whiteSpace: 'nowrap', userSelect: 'none', cursor: 'pointer'
};

const tdStyle = { padding: '10px 12px', fontSize: '0.85rem', verticalAlign: 'middle', borderBottom: '1px solid #f5ede8' };

const inputStyle = { border: '1px solid #d8c8b8', borderRadius: 7, fontSize: '0.85rem', padding: '6px 10px' };

const btnSm = (color, bg = 'transparent') => ({
  border: `1px solid ${color}`, background: bg, color: bg === 'transparent' ? color : '#fff',
  borderRadius: 6, padding: '4px 9px', fontSize: '0.8rem', cursor: 'pointer', lineHeight: 1.4,
  display: 'inline-flex', alignItems: 'center', gap: 4
});

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ─── UserAvatarSmall ─────────────────────────────────────────────────────── */
const UserAvatarSmall = ({ docId, name, size = 32 }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!docId) { setPhotoUrl(null); return; }
    let revoked = false;
    let blobUrl = null;
    documentService.getDocumentObjectUrl(docId)
      .then(url => {
        if (revoked) { URL.revokeObjectURL(url); return; }
        blobUrl = url;
        setPhotoUrl(url);
      })
      .catch(() => setPhotoUrl(null));
    return () => { revoked = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [docId]);

  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      border: '2px solid #6d4e36', overflow: 'hidden',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#e8ddd4', verticalAlign: 'middle',
      fontSize: Math.round(size * 0.32), fontWeight: 'bold', color: '#6d4e36', marginRight: 8
    }}>
      {photoUrl ? <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
    </div>
  );
};

/* ─── PrivilegeTree ───────────────────────────────────────────────────────── */
const PrivilegeTree = ({ privs, onChange, users, excludeUserId }) => {
  const [collapsed, setCollapsed] = useState(new Set());
  const [copyFromUser, setCopyFromUser] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [query, setQuery] = useState('');

  const toggle = (key) => onChange(togglePrivKey(key, privs));

  const toggleSection = (label) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const handleCopy = async () => {
    if (!copyFromUser) return;
    setCopyLoading(true);
    try {
      const { data } = await api.get(`/user-privileges/${copyFromUser.value}`);
      const granted = (data.privileges || []).filter(p => p.is_granted !== false).map(p => p.privilege_key);
      onChange(new Set(granted));
      setCopyFromUser(null);
    } catch { /* ignore */ }
    setCopyLoading(false);
  };

  const q = query.trim().toLowerCase();
  const visibleSections = SECTIONS
    .map(sec => ({ ...sec, modules: sec.modules.filter(m => matchesQuery(m, q)) }))
    .filter(sec => sec.modules.length > 0);

  const readCount = ALL_READ_KEYS.filter(k => privs.has(k)).length;

  return (
    <div>
      <div style={{ background: '#f8f3ef', border: '1px solid #6d4e3622', borderRadius: 8, padding: '7px 12px', marginBottom: 12, fontSize: '0.79rem', color: '#6d4e36' }}>
        Sans privilèges définis, l'accès est basé sur le rôle. Dès qu'un privilège est défini, seules les pages cochées sont accessibles.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <FaSearch style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '0.72rem' }} />
          <input
            placeholder="Rechercher un module…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 26, width: '100%', fontSize: '0.8rem' }}
          />
        </div>
        <span style={{ fontSize: '0.77rem', color: MAIN, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {readCount} / {ALL_READ_KEYS.length} modules
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', background: '#f5f0ff', border: '1px solid #6f42c122', borderRadius: 8, padding: '7px 12px' }}>
        <span style={{ fontSize: '0.78rem', color: '#6f42c1', fontWeight: 600, whiteSpace: 'nowrap' }}>📋 Copier depuis :</span>
        <div style={{ flex: '1 1 200px', minWidth: 160 }}>
          <Select
            options={users.filter(u => u.id !== excludeUserId).map(u => ({ value: u.id, label: `${u.name} (${u.login})` }))}
            value={copyFromUser}
            onChange={setCopyFromUser}
            placeholder="Choisir un utilisateur…"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, fontSize: '0.81rem', borderColor: '#6f42c155', minHeight: 30 }) }}
            noOptionsMessage={() => 'Aucun utilisateur'}
          />
        </div>
        <button onClick={handleCopy} disabled={!copyFromUser || copyLoading}
          style={{ border: '1px solid #6f42c1', background: '#6f42c1', color: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', cursor: copyFromUser ? 'pointer' : 'not-allowed', opacity: copyFromUser ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {copyLoading ? <Spinner size="sm" animation="border" /> : '📋 Copier'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', color: '#888' }}>Sélection rapide :</span>
        <button onClick={() => onChange(new Set(ALL_PRIV_KEYS))}
          style={{ border: '1px solid #6d4e36', background: 'transparent', color: '#6d4e36', borderRadius: 6, padding: '3px 9px', fontSize: '0.77rem', cursor: 'pointer' }}>
          <FaCheckSquare size={10} className="me-1" /> Tout
        </button>
        <button onClick={() => onChange(new Set())}
          style={{ border: '1px solid #aaa', background: 'transparent', color: '#666', borderRadius: 6, padding: '3px 9px', fontSize: '0.77rem', cursor: 'pointer' }}>
          <FaRegSquare size={10} className="me-1" /> Aucun
        </button>
      </div>

      {visibleSections.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '1.5rem', fontSize: '0.85rem' }}>
          Aucun module ne correspond à « {query} »
        </div>
      ) : visibleSections.map(sec => {
        const secKeys = allSecKeys(sec);
        const isCollapsed = collapsed.has(sec.label);
        const checkedCount = sec.modules.filter(m => privs.has(m.key)).length;
        const someChecked = secKeys.some(k => privs.has(k));
        const allChecked = secKeys.length > 0 && secKeys.every(k => privs.has(k));

        return (
          <div key={sec.label} style={{ marginBottom: 8, border: `1px solid ${sec.color}22`, borderRadius: 8, overflow: 'hidden' }}>
            <div
              onClick={() => toggleSection(sec.label)}
              style={{ background: `${sec.color}10`, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderBottom: isCollapsed ? 'none' : `1px solid ${sec.color}22` }}
            >
              <span style={{ color: sec.color, fontSize: '0.7rem', userSelect: 'none', width: 14 }}>
                {isCollapsed ? <FaChevronRight size={10} /> : <FaChevronDown size={10} />}
              </span>
              {allChecked
                ? <FaCheckSquare color={sec.color} size={13} />
                : someChecked
                  ? <FaCheckSquare color={sec.color} size={13} style={{ opacity: 0.4 }} />
                  : <FaRegSquare color="#bbb" size={13} />}
              <span style={{ fontWeight: 700, color: sec.color, fontSize: '0.82rem', flex: 1 }}>{sec.label}</span>
              <span style={{ fontSize: '0.71rem', color: '#aaa' }}>{checkedCount}/{sec.modules.length}</span>
              <button onClick={e => { e.stopPropagation(); onChange(new Set([...privs, ...secKeys])); }}
                style={{ border: `1px solid ${sec.color}`, background: 'transparent', color: sec.color, borderRadius: 5, padding: '1px 7px', fontSize: '0.7rem', cursor: 'pointer' }}
                title="Tout cocher dans cette section">✓</button>
              <button onClick={e => { e.stopPropagation(); onChange(new Set([...privs].filter(k => !secKeys.includes(k)))); }}
                style={{ border: '1px solid #ccc', background: 'transparent', color: '#888', borderRadius: 5, padding: '1px 7px', fontSize: '0.7rem', cursor: 'pointer' }}
                title="Tout décocher dans cette section">✗</button>
            </div>

            {!isCollapsed && (
              <div style={{ padding: '4px 0' }}>
                {sec.modules.map(mod => {
                  const isChecked = privs.has(mod.key);
                  return (
                    <div key={mod.key}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', background: isChecked ? `${sec.color}06` : 'transparent', cursor: 'pointer' }}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggle(mod.key)}
                          style={{ accentColor: sec.color, width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                        <span style={{ fontWeight: isChecked ? 600 : 400, color: isChecked ? '#222' : '#888', fontSize: '0.82rem', flex: 1 }}>
                          {mod.label}
                          {mod.sublabel && <span style={{ fontWeight: 400, color: '#aaa', fontSize: '0.73rem', marginLeft: 6 }}>{mod.sublabel}</span>}
                        </span>
                      </label>
                      {mod.children.map(child => {
                        const childChecked = privs.has(child.key);
                        return (
                          <label key={child.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 12px 3px 32px', background: childChecked ? `${sec.color}04` : 'transparent', cursor: 'pointer' }}>
                            <input type="checkbox" checked={childChecked} onChange={() => toggle(child.key)}
                              style={{ accentColor: sec.color, width: 13, height: 13, cursor: 'pointer', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.79rem', color: childChecked ? '#444' : '#aaa', fontWeight: childChecked ? 500 : 400 }}>
                              {child.icon} {child.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── composant principal ─────────────────────────────────────────────────── */
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  const [formMode, setFormMode] = useState('add');
  const [formData, setFormData] = useState({ name: '', login: '', password: '', role: 'pointeur', observation: '' });
  const [showPass, setShowPass] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitIp, setRateLimitIp] = useState('');

  const [activeKpiFilter, setActiveKpiFilter] = useState('total');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortAsc, setSortAsc] = useState(true);

  /* ── panneau accordéon inline (privilèges + rôle + affectations) ── */
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [panelTab, setPanelTab] = useState('privileges');
  const [panelPrivs, setPanelPrivs] = useState(new Set());
  const [panelInitialPrivs, setPanelInitialPrivs] = useState(new Set());
  const [panelRole, setPanelRole] = useState('');
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelSaving, setPanelSaving] = useState(false);
  const [panelRoleSaving, setPanelRoleSaving] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [panelSuccess, setPanelSuccess] = useState('');
  const [panelShowClearConfirm, setPanelShowClearConfirm] = useState(false);
  const [panelAffectations, setPanelAffectations] = useState([]);
  const [panelSelectedChantier, setPanelSelectedChantier] = useState(null);
  const [panelAffectSaving, setPanelAffectSaving] = useState(false);

  const [chantiers, setChantiers] = useState([]);

  /* ── sélection multi-utilisateurs ── */
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  /* ── avatars & modal photo ── */
  const [avatarsMap, setAvatarsMap] = useState({});
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  /* ── modal bulk droits & chantiers ── */
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkTab, setBulkTab] = useState('privileges');
  const [bulkPrivs, setBulkPrivs] = useState(new Set());
  const [bulkChantierIds, setBulkChantierIds] = useState([]);
  const [bulkReplaceChantiers, setBulkReplaceChantiers] = useState(true);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkSuccess, setBulkSuccess] = useState('');

  /* ── fetch ── */
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    api.get('/users/avatars')
      .then(r => {
        const map = {};
        (r.data || []).forEach(u => { if (u.photo_profil) map[u.id] = u.photo_profil; });
        setAvatarsMap(map);
      })
      .catch(() => {});
  }, []);

  const ensureChantiers = useCallback(async () => {
    if (chantiers.length) return chantiers;
    try {
      const res = await api.get('/chantiers');
      const list = Array.isArray(res.data) ? res.data : [];
      setChantiers(list);
      return list;
    } catch {
      return [];
    }
  }, [chantiers]);

  /* ── panneau : ouvrir / fermer ── */
  const openPanel = useCallback(async (u) => {
    if (expandedUserId === u.id) { setExpandedUserId(null); return; }

    setExpandedUserId(u.id);
    setPanelTab('privileges');
    setPanelRole(u.role);
    setPanelPrivs(new Set());
    setPanelInitialPrivs(new Set());
    setPanelError('');
    setPanelSuccess('');
    setPanelShowClearConfirm(false);
    setPanelAffectations([]);
    setPanelSelectedChantier(null);
    setPanelLoading(true);

    try {
      const [privRes, affRes] = await Promise.all([
        api.get(`/user-privileges/${u.id}`),
        api.get('/user_chantiers'),
      ]);
      const granted = (privRes.data.privileges || []).filter(p => p.is_granted !== false).map(p => p.privilege_key);
      setPanelPrivs(new Set(granted));
      setPanelInitialPrivs(new Set(granted));

      const allAff = Array.isArray(affRes.data) ? affRes.data : [];
      setPanelAffectations(allAff.filter(a => a.user_id === u.id || a.user_login === u.login));

      ensureChantiers();
    } catch {
      setPanelError('Erreur lors du chargement des droits.');
    } finally {
      setPanelLoading(false);
    }
  }, [expandedUserId, ensureChantiers]);

  const closePanel = () => setExpandedUserId(null);

  const panelDirty = useMemo(() => {
    if (panelPrivs.size !== panelInitialPrivs.size) return true;
    for (const k of panelPrivs) if (!panelInitialPrivs.has(k)) return true;
    return false;
  }, [panelPrivs, panelInitialPrivs]);

  /* ── panneau : privilèges ── */
  const handlePanelSavePrivileges = async () => {
    setPanelSaving(true);
    setPanelError('');
    try {
      await api.put(`/user-privileges/${expandedUserId}`, { privileges: [...panelPrivs] });
      setPanelInitialPrivs(new Set(panelPrivs));
      setPanelSuccess('✅ Privilèges enregistrés.');
    } catch (err) {
      setPanelError(err.response?.data?.message || 'Erreur sauvegarde privilèges.');
    } finally {
      setPanelSaving(false);
    }
  };

  const handlePanelClearPrivileges = async () => {
    setPanelSaving(true);
    setPanelError('');
    try {
      await api.delete(`/user-privileges/${expandedUserId}`);
      setPanelPrivs(new Set());
      setPanelInitialPrivs(new Set());
      setPanelSuccess('Privilèges supprimés — accès basé sur le rôle.');
      setPanelShowClearConfirm(false);
    } catch {
      setPanelError('Erreur suppression privilèges.');
    } finally {
      setPanelSaving(false);
    }
  };

  /* ── panneau : rôle ── */
  const handlePanelSaveRole = async () => {
    const u = users.find(x => x.id === expandedUserId);
    if (!u || panelRole === u.role) return;
    setPanelRoleSaving(true);
    setPanelError('');
    try {
      await axios.put(`/users/${expandedUserId}`, { role: panelRole });
      setUsers(prev => prev.map(x => x.id === expandedUserId ? { ...x, role: panelRole } : x));
      setPanelSuccess(`Rôle changé en "${roleInfo[panelRole]?.label || panelRole}".`);
    } catch (err) {
      setPanelError(err.response?.data?.message || 'Erreur changement de rôle.');
    } finally {
      setPanelRoleSaving(false);
    }
  };

  /* ── panneau : affectations ── */
  const handlePanelAddAffectation = async () => {
    if (!panelSelectedChantier) return;
    setPanelAffectSaving(true);
    setPanelError('');
    try {
      await api.post('/user_chantiers', { user_id: expandedUserId, chantier_id: panelSelectedChantier.value });
      const { data } = await api.get('/user_chantiers');
      const all = Array.isArray(data) ? data : [];
      setPanelAffectations(all.filter(a => a.user_id === expandedUserId));
      setPanelSelectedChantier(null);
      setPanelSuccess('Affectation ajoutée.');
    } catch (err) {
      setPanelError(err.response?.data?.message || 'Erreur ajout affectation.');
    } finally {
      setPanelAffectSaving(false);
    }
  };

  const handlePanelDeleteAffectation = async (affId) => {
    if (!window.confirm('Supprimer cette affectation ?')) return;
    setPanelError('');
    try {
      await api.delete(`/user_chantiers/${affId}`);
      setPanelAffectations(prev => prev.filter(a => a.id !== affId));
      setPanelSuccess('Affectation supprimée.');
    } catch {
      setPanelError('Erreur suppression affectation.');
    }
  };

  /* ── sélection helpers ── */
  const toggleSelectUser = (id) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size > 0 && selectedUserIds.size === displayed.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(displayed.map(u => u.id)));
    }
  };

  /* ── bulk modal ── */
  const openBulkModal = async () => {
    setBulkTab('privileges');
    setBulkPrivs(new Set());
    setBulkChantierIds([]);
    setBulkError('');
    setBulkSuccess('');
    setBulkReplaceChantiers(true);
    setShowBulkModal(true);
    ensureChantiers();
  };

  const handleBulkApplyPrivileges = async () => {
    setBulkSaving(true);
    setBulkError('');
    setBulkSuccess('');
    const ids = [...selectedUserIds];
    try {
      await Promise.all(ids.map(uid => api.put(`/user-privileges/${uid}`, { privileges: [...bulkPrivs] })));
      setBulkSuccess(`✅ Privilèges appliqués à ${ids.length} utilisateur(s).`);
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Erreur lors de l\'application des privilèges.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkApplyChantiers = async () => {
    setBulkSaving(true);
    setBulkError('');
    setBulkSuccess('');
    const ids = [...selectedUserIds];
    try {
      const { data: allAff } = await api.get('/user_chantiers');
      const allAssignments = Array.isArray(allAff) ? allAff : [];
      for (const uid of ids) {
        const userAff = allAssignments.filter(a => a.user_id === uid);
        if (bulkReplaceChantiers) {
          await Promise.all(userAff.map(a => api.delete(`/user_chantiers/${a.id}`).catch(() => {})));
          await Promise.all(bulkChantierIds.map(cid =>
            api.post('/user_chantiers', { user_id: uid, chantier_id: cid }).catch(() => {})
          ));
        } else {
          const existingCids = userAff.map(a => a.chantier_id);
          const toAdd = bulkChantierIds.filter(cid => !existingCids.includes(cid));
          await Promise.all(toAdd.map(cid =>
            api.post('/user_chantiers', { user_id: uid, chantier_id: cid }).catch(() => {})
          ));
        }
      }
      setBulkSuccess(`✅ Chantiers appliqués à ${ids.length} utilisateur(s).`);
    } catch (err) {
      setBulkError(err.response?.data?.message || 'Erreur lors de l\'application des chantiers.');
    } finally {
      setBulkSaving(false);
    }
  };

  /* ── modal photo ── */
  const openPhotoModal = (u) => {
    setPhotoTarget({ id: u.id, name: u.name, photo_profil: avatarsMap[u.id] || null });
    setPhotoError('');
    setShowPhotoModal(true);
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !photoTarget) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/users/${photoTarget.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newDocId = res.data.photo_profil_id;
      setAvatarsMap(prev => ({ ...prev, [photoTarget.id]: newDocId }));
      setPhotoTarget(prev => ({ ...prev, photo_profil: newDocId }));
    } catch (err) {
      setPhotoError(err.response?.data?.message || "Erreur lors de l'upload");
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!photoTarget) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      await api.delete(`/users/${photoTarget.id}/photo`);
      setAvatarsMap(prev => { const n = { ...prev }; delete n[photoTarget.id]; return n; });
      setPhotoTarget(prev => ({ ...prev, photo_profil: null }));
    } catch (err) {
      setPhotoError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setPhotoUploading(false);
    }
  };

  /* ── modals ── */
  const openAdd = () => {
    setFormMode('add');
    setSelectedUser(null);
    setFormData({ name: '', login: '', password: '', role: 'pointeur', observation: '' });
    setShowPass(false);
    setShowFormModal(true);
  };

  const openEdit = (u) => {
    setFormMode('edit');
    setSelectedUser(u);
    setFormData({ name: u.name, login: u.login, password: '', role: u.role, observation: u.observation || '' });
    setShowPass(false);
    setShowFormModal(true);
  };

  /* ── actions ── */
  const handleFormSubmit = async () => {
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      const payload = { ...formData };
      if (formMode === 'edit' && !payload.password) delete payload.password;
      if (payload.password !== undefined) {
        const strengthError = checkPasswordStrength(payload.password, payload.login);
        if (strengthError) {
          setError(`⚠️ ${strengthError}`);
          return;
        }
      }
      if (formMode === 'add') {
        await axios.post('/users', payload);
        setSuccess('Utilisateur ajouté avec succès');
      } else {
        await axios.put(`/users/${selectedUser.id}`, payload);
        setSuccess('Utilisateur modifié avec succès');
      }
      setShowFormModal(false);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur enregistrement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      await axios.delete(`/users/${selectedUser.id}`);
      setShowDeleteModal(false);
      setSuccess('Utilisateur supprimé avec succès');
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async () => {
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      await axios.patch(`/users/${selectedUser.id}/block`, { reason: blockReason });
      setShowBlockModal(false);
      setSuccess('Utilisateur bloqué');
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur blocage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async (u) => {
    if (!window.confirm(`Débloquer "${u.name}" ?`)) return;
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      await axios.patch(`/users/${u.id}/unblock`);
      setSuccess('Utilisateur débloqué');
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur déblocage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetLock = async (u) => {
    if (!window.confirm(`Réinitialiser le verrouillage de "${u.name}" ?`)) return;
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      await axios.patch(`/users/${u.id}/reset-lock`);
      setSuccess(`Verrouillage de ${u.name} réinitialisé`);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur réinitialisation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetRateLimit = async () => {
    const ip = rateLimitIp.trim();
    if (!ip) return;
    try {
      setActionLoading(true);
      setError(''); setSuccess('');
      await axios.post('/users/reset-rate-limit', { ip });
      setSuccess(`Limite de connexion réinitialisée pour ${ip}`);
      setShowRateLimitModal(false);
      setRateLimitIp('');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur réinitialisation IP');
    } finally {
      setActionLoading(false);
    }
  };

  /* ── stats ── */
  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.is_active).length,
    blocked: users.filter(u => !u.is_active).length,
    admins: users.filter(u => u.role === 'admin').length,
    resp_budget: users.filter(u => u.role === 'responsable_budget').length,
    achat: users.filter(u => u.role === 'achat').length,
    resp_commercial: users.filter(u => u.role === 'resp_commercial').length
  }), [users]);

  /* ── filtres ── */
  const displayed = useMemo(() => {
    let list = [...users];

    if (activeKpiFilter === 'active') list = list.filter(u => u.is_active);
    else if (activeKpiFilter === 'blocked') list = list.filter(u => !u.is_active);
    else if (activeKpiFilter === 'admins') list = list.filter(u => u.role === 'admin');
    else if (activeKpiFilter === 'resp_budget') list = list.filter(u => u.role === 'responsable_budget');
    else if (activeKpiFilter === 'achat') list = list.filter(u => u.role === 'achat');
    else if (activeKpiFilter === 'resp_commercial') list = list.filter(u => u.role === 'resp_commercial');

    if (filterRole) list = list.filter(u => u.role === filterRole);
    if (filterStatus === 'active') list = list.filter(u => u.is_active);
    if (filterStatus === 'blocked') list = list.filter(u => !u.is_active);

    const q = search.trim().toLowerCase();
    if (q) list = list.filter(u =>
      u.name?.toLowerCase().includes(q) ||
      u.login?.toLowerCase().includes(q)
    );

    list.sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
      return sortAsc
        ? String(av).localeCompare(String(bv), 'fr')
        : String(bv).localeCompare(String(av), 'fr');
    });

    return list;
  }, [users, activeKpiFilter, filterRole, filterStatus, search, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortTh = ({ field, children, style }) => (
    <th onClick={() => handleSort(field)} style={{ ...thStyle, ...style }}>
      {children}
      <span style={{ marginLeft: 4, fontSize: '0.6rem', opacity: sortKey === field ? 1 : 0.35 }}>
        {sortKey === field ? (sortAsc ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );

  const hasFilters = search || filterRole || filterStatus || activeKpiFilter !== 'total';
  const panelUser = users.find(u => u.id === expandedUserId) || null;

  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: '1rem 1rem 3rem' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: '1.25rem' }}>
        <div>
          <h4 style={{ color: MAIN, fontWeight: 700, marginBottom: 2 }}>Gestion des utilisateurs</h4>
          <div style={{ color: '#888', fontSize: '0.82rem' }}>
            {users.length} utilisateur{users.length !== 1 ? 's' : ''} au total — cliquez une ligne pour gérer ses droits
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchUsers} disabled={loading} style={btnSm(MAIN)}>
            {loading ? <Spinner size="sm" animation="border" /> : <FaSyncAlt />}
            Actualiser
          </button>
          <button onClick={() => { setRateLimitIp(''); setShowRateLimitModal(true); }} style={btnSm('#e67e22')} title="Réinitialiser la limite de connexion pour une IP">
            <FaNetworkWired /> Débloquer IP
          </button>
          {selectedUserIds.size > 0 && (
            <button onClick={openBulkModal} style={{ ...btnSm('#fff', '#6f42c1'), fontWeight: 700 }}>
              <FaShieldAlt /> Appliquer aux {selectedUserIds.size} sélectionné{selectedUserIds.size > 1 ? 's' : ''}
            </button>
          )}
          <button onClick={openAdd} style={{ ...btnSm('#fff', MAIN), fontWeight: 700 }}>
            <FaPlus /> Ajouter
          </button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Total" value={stats.total} color={MAIN} icon={FaUsers}
            onClick={() => setActiveKpiFilter('total')} active={activeKpiFilter === 'total'} />
        </Col>
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Actifs" value={stats.active} color="#2e7d32" icon={FaCheckCircle}
            onClick={() => setActiveKpiFilter('active')} active={activeKpiFilter === 'active'} />
        </Col>
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Bloqués" value={stats.blocked} color="#dc3545" icon={FaLock}
            onClick={() => setActiveKpiFilter('blocked')} active={activeKpiFilter === 'blocked'} alert={stats.blocked > 0} />
        </Col>
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Admins" value={stats.admins} color="#dc3545" icon={FaUserShield}
            onClick={() => setActiveKpiFilter('admins')} active={activeKpiFilter === 'admins'} />
        </Col>
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Resp. Budget" value={stats.resp_budget} color="#6f42c1" icon={FaMoneyBillWave}
            onClick={() => setActiveKpiFilter('resp_budget')} active={activeKpiFilter === 'resp_budget'} />
        </Col>
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Achat" value={stats.achat} color="#0d9488" icon={FaSearch}
            onClick={() => setActiveKpiFilter('achat')} active={activeKpiFilter === 'achat'} />
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div style={card}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f5ede8', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <FaFilter style={{ color: MAIN, fontSize: '0.8rem', flexShrink: 0 }} />
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 150 }}>
            <FaSearch style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '0.75rem' }} />
            <input placeholder="Rechercher nom ou login…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 28, width: '100%' }} />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...inputStyle, flex: '0 1 170px' }}>
            <option value="">Tous les rôles</option>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, flex: '0 1 130px' }}>
            <option value="">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="blocked">Bloqués</option>
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setFilterRole(''); setFilterStatus(''); setActiveKpiFilter('total'); }} style={btnSm('#888')}>
              Réinitialiser
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#888' }}>
            {displayed.length} ligne{displayed.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}><Spinner animation="border" style={{ color: MAIN }} /></div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa', fontSize: '0.9rem' }}>Aucun utilisateur trouvé</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 40, cursor: 'default', textAlign: 'center' }}>
                    <input type="checkbox"
                      checked={selectedUserIds.size > 0 && selectedUserIds.size === displayed.length}
                      ref={el => { if (el) el.indeterminate = selectedUserIds.size > 0 && selectedUserIds.size < displayed.length; }}
                      onChange={toggleSelectAll}
                      onClick={e => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: 15, height: 15 }} />
                  </th>
                  <SortTh field="id" style={{ width: 55 }}>#</SortTh>
                  <SortTh field="name">Nom</SortTh>
                  <SortTh field="login">Login</SortTh>
                  <SortTh field="role">Rôle</SortTh>
                  <SortTh field="is_active">Statut</SortTh>
                  <SortTh field="last_login_at">Dernière connexion</SortTh>
                  <SortTh field="failed_login_attempts" style={{ width: 80 }}>Échecs</SortTh>
                  <th style={{ ...thStyle, cursor: 'default', textAlign: 'center', width: 150 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((u, i) => (
                  <tr key={u.id} style={{ background: selectedUserIds.has(u.id) ? '#f0ebff' : i % 2 === 0 ? '#fff' : '#fdf9f7' }}>
                    <td style={{ ...tdStyle, textAlign: 'center', width: 40 }}>
                      <input type="checkbox" checked={selectedUserIds.has(u.id)} onChange={() => toggleSelectUser(u.id)}
                        style={{ cursor: 'pointer', width: 15, height: 15 }} />
                    </td>
                    <td style={{ ...tdStyle, color: '#aaa', fontWeight: 600, fontSize: '0.78rem' }}>{u.id}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <UserAvatarSmall docId={avatarsMap[u.id] || null} name={u.name} size={30} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#333' }}>{u.name}</div>
                          {u.observation && <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 2 }}>{u.observation}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#555' }}>{u.login}</td>
                    <td style={tdStyle}><RoleBadge role={u.role} /></td>
                    <td style={tdStyle}>
                      {u.is_active
                        ? <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.8rem' }}>Actif</span>
                        : (
                          <div>
                            <span style={{ color: '#dc3545', fontWeight: 600, fontSize: '0.8rem' }}>Bloqué</span>
                            {u.blocked_reason && <div style={{ fontSize: '0.71rem', color: '#dc3545', opacity: 0.8, marginTop: 1 }}>{u.blocked_reason}</div>}
                          </div>
                        )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '0.8rem', color: '#555' }}>{fmtDate(u.last_login_at)}</div>
                      {u.last_login_ip && <div style={{ fontSize: '0.71rem', color: '#aaa' }}>{u.last_login_ip}</div>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {u.failed_login_attempts > 0 ? <Badge bg="warning" text="dark">{u.failed_login_attempts}</Badge> : <span style={{ color: '#ccc' }}>—</span>}
                      {u.locked_until && <div style={{ fontSize: '0.68rem', color: '#e67e22', marginTop: 2 }}>jusqu'à {fmtDate(u.locked_until)}</div>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                        <button title="Modifier" onClick={() => openEdit(u)} style={btnSm('#0d6efd')}><FaEdit /></button>
                        {u.is_active
                          ? <button title="Bloquer" onClick={() => { setSelectedUser(u); setBlockReason(u.blocked_reason || ''); setShowBlockModal(true); }} style={btnSm('#e67e22')}><FaLock /></button>
                          : <button title="Débloquer" onClick={() => handleUnblock(u)} style={btnSm('#2e7d32')}><FaUnlock /></button>}
                        {u.is_active && u.locked_until && new Date(u.locked_until) > new Date() && (
                          <button title="Réinitialiser le verrouillage" onClick={() => handleResetLock(u)} style={btnSm('#e67e22')}><FaKey /></button>
                        )}
                        <button title="Photo de profil" onClick={() => openPhotoModal(u)} style={btnSm('#6d4e36')}><FaCamera /></button>
                        <button title="Droits & Chantiers" onClick={() => openPanel(u)} style={expandedUserId === u.id ? btnSm('#fff', '#6f42c1') : btnSm('#6f42c1')}><FaShieldAlt /></button>
                        <button title="Supprimer" onClick={() => { setSelectedUser(u); setShowDeleteModal(true); }} style={btnSm('#dc3545')}><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════ PANNEAU LATÉRAL — DROITS & CHANTIERS ══════════ */}
      <Offcanvas show={!!panelUser} onHide={closePanel} placement="end" backdrop scroll={false}
        style={{ width: 'min(560px, 100vw)' }}>
        {panelUser && (
          <>
            <Offcanvas.Header closeButton style={{ borderBottom: '3px solid #6f42c1', background: '#faf8ff', padding: '1rem 1.25rem' }}>
              <Offcanvas.Title style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                <UserAvatarSmall docId={avatarsMap[panelUser.id] || null} name={panelUser.name} size={38} />
                <div>
                  <div style={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }}>{panelUser.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>{panelUser.login}</div>
                </div>
              </Offcanvas.Title>
            </Offcanvas.Header>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 1.25rem', background: '#faf8ff', borderBottom: '1px solid #e8e0f0' }}>
              <span style={{ fontSize: '0.78rem', color: '#6f42c1', fontWeight: 600 }}>Rôle :</span>
              <select value={panelRole} onChange={e => setPanelRole(e.target.value)} style={{ ...inputStyle, padding: '4px 8px', fontSize: '0.8rem', flex: 1 }}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {panelRole !== panelUser.role && (
                <button onClick={handlePanelSaveRole} disabled={panelRoleSaving}
                  style={{ ...btnSm('#fff', '#6f42c1'), fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {panelRoleSaving ? <Spinner size="sm" animation="border" /> : '✓ Appliquer'}
                </button>
              )}
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid #e0d4f5', padding: '0 1.25rem', background: '#fff' }}>
              {[
                { key: 'privileges', label: '🔐 Privilèges' },
                { key: 'affectations', label: '🏗️ Chantiers' }
              ].map(tab => (
                <button key={tab.key} onClick={() => { setPanelTab(tab.key); setPanelError(''); setPanelSuccess(''); setPanelShowClearConfirm(false); }}
                  style={{
                    padding: '10px 16px', fontSize: '0.82rem', fontWeight: panelTab === tab.key ? 700 : 500,
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    borderBottom: panelTab === tab.key ? '3px solid #6f42c1' : '3px solid transparent',
                    color: panelTab === tab.key ? '#6f42c1' : '#888'
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>

            <Offcanvas.Body style={{ padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column' }}>
              {panelError && <Alert variant="danger" dismissible onClose={() => setPanelError('')} className="py-2">{panelError}</Alert>}
              {panelSuccess && <Alert variant="success" dismissible onClose={() => setPanelSuccess('')} className="py-2">{panelSuccess}</Alert>}

              {panelLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" style={{ color: '#6f42c1' }} /></div>
              ) : panelTab === 'privileges' ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                    <PrivilegeTree privs={panelPrivs} onChange={setPanelPrivs} users={users} excludeUserId={panelUser.id} />

                    {panelShowClearConfirm && (
                      <Alert variant="warning" className="mt-2">
                        <strong>⚠️ Confirmer la suppression ?</strong><br />
                        Les privilèges de <strong>{panelUser.name}</strong> seront supprimés. L'accès redeviendra basé sur son rôle.
                        <div className="mt-2 d-flex gap-2">
                          <Button size="sm" variant="danger" onClick={handlePanelClearPrivileges} disabled={panelSaving}>
                            {panelSaving ? <Spinner size="sm" /> : '🗑️ Confirmer'}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setPanelShowClearConfirm(false)}>Annuler</Button>
                        </div>
                      </Alert>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', borderTop: '1px solid #f0e8f8', paddingTop: 12, flexWrap: 'wrap' }}>
                    <button onClick={() => setPanelShowClearConfirm(true)} disabled={panelSaving || panelShowClearConfirm}
                      style={{ border: '1px solid #dc3545', background: 'transparent', color: '#dc3545', borderRadius: 7, padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <FaTrash size={11} /> Supprimer
                    </button>
                    <div style={{ flex: 1 }} />
                    {panelDirty && <span style={{ fontSize: '0.76rem', color: '#e67e22', whiteSpace: 'nowrap' }}>● Non enregistré</span>}
                    <Button onClick={handlePanelSavePrivileges} disabled={panelSaving || !panelDirty}
                      style={{ background: '#6f42c1', border: 'none', borderRadius: 7, minWidth: 130 }}>
                      {panelSaving ? <><Spinner size="sm" className="me-2" />Enregistrement…</> : '✅ Sauvegarder'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px', minWidth: 160 }}>
                      <Select
                        options={chantiers.filter(c => !panelAffectations.some(a => a.chantier_id === c.id)).map(c => ({ value: c.id, label: c.name || `Chantier #${c.id}` }))}
                        value={panelSelectedChantier}
                        onChange={setPanelSelectedChantier}
                        placeholder="🔎 Choisir un chantier…"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), control: b => ({ ...b, fontSize: '0.83rem', borderColor: '#d8c8b8' }) }}
                        noOptionsMessage={() => 'Aucun chantier disponible'}
                      />
                    </div>
                    <button onClick={handlePanelAddAffectation} disabled={!panelSelectedChantier || panelAffectSaving}
                      style={{ ...btnSm('#fff', '#6f42c1'), fontWeight: 700, opacity: !panelSelectedChantier ? 0.5 : 1 }}>
                      {panelAffectSaving ? <Spinner size="sm" animation="border" /> : <><FaLink /> Affecter</>}
                    </button>
                  </div>

                  {panelAffectations.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#aaa', padding: '1.5rem', fontSize: '0.88rem' }}>
                      <FaHardHat style={{ fontSize: '1.8rem', marginBottom: 8, opacity: 0.3, display: 'block', margin: '0 auto 8px' }} />
                      Aucune affectation chantier
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {panelAffectations.map(aff => (
                        <div key={aff.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#faf8ff', border: '1px solid #e8e0f0', borderRadius: 8, padding: '8px 12px' }}>
                          <FaHardHat style={{ color: '#6f42c1', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: '#333', flex: 1 }}>{aff.chantier_name || `Chantier #${aff.chantier_id}`}</span>
                          <button onClick={() => handlePanelDeleteAffectation(aff.id)} style={btnSm('#dc3545')} title="Supprimer"><FaTrash /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Offcanvas.Body>
          </>
        )}
      </Offcanvas>

      {/* ══════════ MODAL PHOTO DE PROFIL ══════════ */}
      <Modal show={showPhotoModal} onHide={() => { setShowPhotoModal(false); setPhotoError(''); }} centered size="sm">
        <Modal.Header closeButton style={{ backgroundColor: '#f5f5db', borderBottom: '2px solid #6d4e36' }}>
          <Modal.Title style={{ color: '#6d4e36', fontSize: '1rem', fontWeight: 700 }}>
            <FaCamera className="me-2" />Photo de profil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4" style={{ backgroundColor: '#fafaf0' }}>
          {photoTarget && (
            <>
              <div style={{ fontSize: '0.9rem', color: '#6d4e36', fontWeight: 700, marginBottom: 16 }}>{photoTarget.name}</div>
              <div style={{ marginBottom: 16 }}>
                <UserAvatarSmall docId={photoTarget.photo_profil} name={photoTarget.name} size={90} />
              </div>
              {photoError && <Alert variant="danger" className="py-1 px-2 small mb-3">{photoError}</Alert>}
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                <label style={{
                  backgroundColor: '#6d4e36', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px',
                  fontSize: '0.82rem', cursor: photoUploading ? 'default' : 'pointer', fontWeight: 600,
                  opacity: photoUploading ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 6
                }}>
                  {photoUploading ? <Spinner animation="border" size="sm" /> : <FaCamera />}
                  {photoTarget.photo_profil ? 'Changer' : 'Ajouter'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} disabled={photoUploading}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); e.target.value = ''; }} />
                </label>
                {photoTarget.photo_profil && (
                  <button onClick={handlePhotoDelete} disabled={photoUploading}
                    style={{ background: 'none', border: '1.5px solid #dc3545', color: '#dc3545', borderRadius: 6, padding: '4px 12px', fontSize: '0.82rem', cursor: photoUploading ? 'default' : 'pointer', fontWeight: 600, opacity: photoUploading ? 0.7 : 1 }}>
                    <FaTrash className="me-1" />Supprimer
                  </button>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ══════════ MODAL AJOUT / MODIFICATION ══════════ */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} centered size="md">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
          <Modal.Title style={{ color: MAIN, fontWeight: 700, fontSize: '1rem' }}>
            {formMode === 'add' ? <><FaPlus className="me-2" />Ajouter un utilisateur</> : <><FaEdit className="me-2" />Modifier l'utilisateur</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem' }}>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Nom complet</Form.Label>
            <Form.Control size="sm" name="name" value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              style={{ borderColor: '#d8c8b8' }} placeholder="Ex : Ahmed Benali" />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Login</Form.Label>
            <Form.Control size="sm" name="login" value={formData.login}
              onChange={e => setFormData(p => ({ ...p, login: e.target.value }))}
              style={{ borderColor: '#d8c8b8', fontFamily: 'monospace' }} placeholder="Ex : a.benali" />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>
              Mot de passe
              {formMode === 'edit' && <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 6 }}>— laisser vide pour ne pas changer</span>}
            </Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control size="sm" type={showPass ? 'text' : 'password'} name="password"
                value={formData.password}
                onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                style={{ borderColor: '#d8c8b8', paddingRight: 36 }} />
              <button type="button" onClick={() => setShowPass(s => !s)}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: 0 }}>
                {showPass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <Form.Text muted style={{ fontSize: '0.75rem' }}>{PASSWORD_RULE_MESSAGE}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Rôle</Form.Label>
            <Form.Select size="sm" name="role" value={formData.role}
              onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
              style={{ borderColor: '#d8c8b8' }}>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
            </Form.Select>
            {formData.role && roleInfo[formData.role] && (
              <div style={{ marginTop: 6 }}><RoleBadge role={formData.role} /></div>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Observation <span style={{ color: '#aaa', fontWeight: 400 }}>(optionnel)</span></Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" name="observation"
              value={formData.observation}
              onChange={e => setFormData(p => ({ ...p, observation: e.target.value }))}
              style={{ borderColor: '#d8c8b8', resize: 'none' }} placeholder="Note interne…" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8', gap: 8 }}>
          <Button variant="light" onClick={() => setShowFormModal(false)} style={{ borderColor: '#d8c8b8' }}>Annuler</Button>
          <Button onClick={handleFormSubmit} disabled={actionLoading} style={{ background: MAIN, border: 'none', fontWeight: 700, minWidth: 110 }}>
            {actionLoading ? <Spinner size="sm" animation="border" /> : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ MODAL SUPPRESSION ══════════ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
          <Modal.Title style={{ color: '#dc3545', fontWeight: 700, fontSize: '1rem' }}>Supprimer l'utilisateur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Confirmer la suppression de <strong>{selectedUser?.name}</strong> ?
          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 6 }}>Cette action est irréversible.</div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8', gap: 8 }}>
          <Button variant="light" onClick={() => setShowDeleteModal(false)} style={{ borderColor: '#d8c8b8' }}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} disabled={actionLoading} style={{ minWidth: 100 }}>
            {actionLoading ? <Spinner size="sm" animation="border" /> : <><FaTrash className="me-1" />Supprimer</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ MODAL BLOCAGE ══════════ */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
          <Modal.Title style={{ color: '#e67e22', fontWeight: 700, fontSize: '1rem' }}><FaLock className="me-2" />Bloquer l'utilisateur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: 12 }}>Bloquer <strong>{selectedUser?.name}</strong> ?</p>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Motif <span style={{ color: '#aaa', fontWeight: 400 }}>(optionnel)</span></Form.Label>
            <Form.Control as="textarea" rows={3} size="sm" value={blockReason} onChange={e => setBlockReason(e.target.value)}
              style={{ borderColor: '#d8c8b8', resize: 'none' }} placeholder="Ex : départ de l'entreprise, suspension…" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8', gap: 8 }}>
          <Button variant="light" onClick={() => setShowBlockModal(false)} style={{ borderColor: '#d8c8b8' }}>Annuler</Button>
          <Button variant="warning" onClick={handleBlock} disabled={actionLoading} style={{ minWidth: 100 }}>
            {actionLoading ? <Spinner size="sm" animation="border" /> : <><FaLock className="me-1" />Bloquer</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ MODAL RESET RATE LIMIT IP ══════════ */}
      <Modal show={showRateLimitModal} onHide={() => setShowRateLimitModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
          <Modal.Title style={{ color: '#e67e22', fontWeight: 700, fontSize: '1rem' }}><FaNetworkWired className="me-2" />Débloquer une adresse IP</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem' }}>
          {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}
          <p style={{ fontSize: '0.83rem', color: '#555', marginBottom: '0.75rem' }}>
            Réinitialise la limite de tentatives de connexion pour une adresse IP bloquée par le pare-feu applicatif.
          </p>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>Adresse IP</Form.Label>
            <Form.Control size="sm" placeholder="ex : 192.168.1.10" value={rateLimitIp}
              onChange={e => setRateLimitIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleResetRateLimit()}
              style={inputStyle} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8' }}>
          <Button variant="secondary" onClick={() => setShowRateLimitModal(false)}>Annuler</Button>
          <Button onClick={handleResetRateLimit} disabled={actionLoading || !rateLimitIp.trim()} style={{ background: '#e67e22', border: 'none', borderRadius: 7, minWidth: 120 }}>
            {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaUnlock className="me-1" />}
            Débloquer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══════════ MODAL BULK DROITS & CHANTIERS ══════════ */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} centered size="xl">
        <Modal.Header closeButton style={{ borderBottom: '3px solid #6f42c1', padding: '0.85rem 1.25rem' }}>
          <Modal.Title style={{ color: '#6f42c1', fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaShieldAlt /> Appliquer aux {selectedUserIds.size} utilisateurs sélectionnés
          </Modal.Title>
        </Modal.Header>

        <div style={{ padding: '8px 16px', background: '#f5f0ff', borderBottom: '1px solid #e8e0f0', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: '#6f42c1', fontWeight: 600, flexShrink: 0 }}>👥 Sélection :</span>
          {[...selectedUserIds].map(id => {
            const u = users.find(x => x.id === id);
            return u ? (
              <span key={id} style={{ background: '#6f42c118', border: '1px solid #6f42c144', borderRadius: 10, padding: '2px 8px', fontSize: '0.75rem', color: '#6f42c1', fontWeight: 600 }}>
                {u.name}
              </span>
            ) : null;
          })}
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #e8e0f0', background: '#faf8ff' }}>
          {[
            { key: 'privileges', label: '🔐 Privilèges' },
            { key: 'chantiers', label: '🏗️ Affectations chantiers' }
          ].map(tab => (
            <button key={tab.key} onClick={() => { setBulkTab(tab.key); setBulkError(''); setBulkSuccess(''); }}
              style={{
                padding: '10px 20px', fontSize: '0.85rem', fontWeight: bulkTab === tab.key ? 700 : 500,
                border: 'none', background: 'transparent', cursor: 'pointer',
                borderBottom: bulkTab === tab.key ? '3px solid #6f42c1' : '3px solid transparent',
                color: bulkTab === tab.key ? '#6f42c1' : '#888'
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        <Modal.Body style={{ padding: '1.25rem', minHeight: 300 }}>
          {bulkError && <Alert variant="danger" dismissible onClose={() => setBulkError('')} className="py-2">{bulkError}</Alert>}
          {bulkSuccess && <Alert variant="success" dismissible onClose={() => setBulkSuccess('')} className="py-2">{bulkSuccess}</Alert>}

          {bulkTab === 'privileges' ? (
            <>
              <div style={{ background: '#f8f3ef', border: '1px solid #6d4e3622', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: '0.8rem', color: '#6d4e36' }}>
                Les privilèges ci-dessous remplaceront ceux de tous les utilisateurs sélectionnés.
              </div>
              <PrivilegeTree privs={bulkPrivs} onChange={setBulkPrivs} users={users} excludeUserId={null} />
            </>
          ) : (
            <>
              <Form.Check
                type="switch" id="bulk-replace-switch"
                label={bulkReplaceChantiers ? 'Remplacer les affectations existantes' : 'Ajouter aux affectations existantes'}
                checked={bulkReplaceChantiers}
                onChange={e => setBulkReplaceChantiers(e.target.checked)}
                className="mb-3"
              />
              <Select
                isMulti
                options={chantiers.map(c => ({ value: c.id, label: c.name || `Chantier #${c.id}` }))}
                value={chantiers.filter(c => bulkChantierIds.includes(c.id)).map(c => ({ value: c.id, label: c.name || `Chantier #${c.id}` }))}
                onChange={opts => setBulkChantierIds((opts || []).map(o => o.value))}
                placeholder="Choisir un ou plusieurs chantiers…"
                menuPortalTarget={document.body}
                styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, fontSize: '0.83rem', borderColor: '#d8c8b8' }) }}
                noOptionsMessage={() => 'Aucun chantier disponible'}
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #f0e8f8', gap: 8 }}>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)} disabled={bulkSaving}>Fermer</Button>
          {bulkTab === 'privileges' ? (
            <Button onClick={handleBulkApplyPrivileges} disabled={bulkSaving} style={{ background: '#6f42c1', border: 'none', borderRadius: 7, minWidth: 130 }}>
              {bulkSaving ? <><Spinner size="sm" className="me-2" />Application…</> : '✅ Appliquer'}
            </Button>
          ) : (
            <Button onClick={handleBulkApplyChantiers} disabled={bulkSaving || bulkChantierIds.length === 0} style={{ background: '#6f42c1', border: 'none', borderRadius: 7, minWidth: 130 }}>
              {bulkSaving ? <><Spinner size="sm" className="me-2" />Application…</> : '✅ Appliquer'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersPage;
